import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as faceapi from 'face-api.js';
import { Camera, UserPlus, Play, Square, CheckCircle, XCircle, Loader2, Trash2, ArrowLeft, LogIn, LogOut, Users, ScanFace, RotateCcw, FlipHorizontal2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import faceService, { FaceEmbedding, FacePunchResponse } from '../services/faceService';
import { employeeService } from '../services';

type Mode = 'attendance' | 'register';
type ScanState = 'loading' | 'idle' | 'scanning' | 'success_in' | 'success_out' | 'error';

const MATCH_THRESHOLD = 0.5;

// Guided registration steps - keys for i18n
const REGISTER_STEP_KEYS = [
  { label: 'onden', instructionKey: 'lookStraight', icon: '😐', descKey: 'lookStraightDesc' },
  { label: 'hafif-sol', instructionKey: 'turnLeft', icon: '👈', descKey: 'turnLeftDesc' },
  { label: 'hafif-sag', instructionKey: 'turnRight', icon: '👉', descKey: 'turnRightDesc' },
  { label: 'yukari', instructionKey: 'lookUp', icon: '👆', descKey: 'lookUpDesc' },
  { label: 'asagi', instructionKey: 'lookDown', icon: '👇', descKey: 'lookDownDesc' },
];

const FaceAttendance: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const [mode, setMode] = useState<Mode>('attendance');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanState, setScanState] = useState<ScanState>('loading');
  const [lastPunch, setLastPunch] = useState<FacePunchResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [embeddings, setEmbeddings] = useState<FaceEmbedding[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayStats, setTodayStats] = useState({ totalIn: 0, totalOut: 0, uniqueEmployees: 0 });
  const [todayLogs, setTodayLogs] = useState<{ employeeName: string; direction: string; time: string; confidence: number }[]>([]);

  // Register state
  const [employees, setEmployees] = useState<{ id: number; name: string; departmentName?: string }[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
  const [capturedDescriptors, setCapturedDescriptors] = useState<{ label: string; values: number[] }[]>([]);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [registerStep, setRegisterStep] = useState(0);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [captureCountdown, setCaptureCountdown] = useState<number | null>(null);
  const [stepComplete, setStepComplete] = useState(false);

  // Camera selection
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<number | null>(null);
  const lastPunchTimeRef = useRef<number>(0);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const faceMatcherRef = useRef<any>(null);
  const autoCaptureRef = useRef<number | null>(null);

  // Saat guncelleme
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load face-api models
  useEffect(() => {
    loadModels();
    loadEmployees();
    return () => stopCamera();
  }, []);

  const [backendName, setBackendName] = useState<string>('');

  const initTfBackend = async (): Promise<boolean> => {
    // Try WebGL first
    try {
      const webglOk = await faceapi.tf.setBackend('webgl');
      if (webglOk !== false) {
        await faceapi.tf.ready();
        if (faceapi.tf.getBackend() === 'webgl') {
          console.log('TF.js backend: webgl');
          setBackendName('webgl');
          return true;
        }
      }
    } catch (e) {
      console.warn('WebGL backend basarisiz:', e);
    }

    // Fallback to CPU
    try {
      // Dispose any partially-initialized WebGL state
      try { faceapi.tf.engine().reset(); } catch { /* ignore */ }

      const cpuOk = await faceapi.tf.setBackend('cpu');
      if (cpuOk !== false) {
        await faceapi.tf.ready();
        if (faceapi.tf.getBackend() === 'cpu') {
          console.log('TF.js backend: cpu (fallback)');
          setBackendName('cpu');
          return true;
        }
      }
    } catch (e) {
      console.warn('CPU backend basarisiz:', e);
    }

    return false;
  };

  const loadModels = async () => {
    try {
      setLoadingModels(true);
      setScanState('loading');

      const backendReady = await initTfBackend();
      if (!backendReady) {
        setErrorMessage(t('faceAttendance.tfBackendError'));
        setScanState('error');
        setLoadingModels(false);
        return;
      }

      const MODEL_URL = '/models';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
      setScanState('idle');
    } catch (err: any) {
      const msg = err?.message || String(err);
      if (msg.includes('404') || msg.includes('fetch')) {
        setErrorMessage(t('faceAttendance.modelFilesError'));
      } else {
        setErrorMessage(`${t('faceAttendance.modelLoadError')}: ${msg}`);
      }
      setScanState('error');
    } finally {
      setLoadingModels(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await employeeService.getAll(true);
      if (res.success && res.data) {
        setEmployees(res.data.map((e: any) => ({
          id: e.employeeId || e.id,
          name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          departmentName: e.departmentName
        })));
      }
    } catch (err) {
      console.error('Personel listesi hatasi:', err);
    }
  };

  const loadEmbeddings = async () => {
    try {
      const res = await faceService.getEmbeddings();
      if (res.success && res.data) {
        setEmbeddings(res.data);
      }
    } catch (err) {
      console.error('Embedding yukleme hatasi:', err);
    }
  };

  // Enumerate cameras
  const enumerateCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(cameras);
    } catch { /* ignore */ }
  };

  // Camera
  const startCamera = async (facing?: 'user' | 'environment') => {
    try {
      setErrorMessage('');
      const useFacing = facing || facingMode;

      // Onceki stream'i kapat
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: useFacing, width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      setCameraActive(true);

      await enumerateCameras();

      if (mode === 'attendance') {
        await loadEmbeddings();
      }
    } catch (err: any) {
      setErrorMessage(`${t('faceAttendance.cameraError')}: ${err.message}`);
      setScanState('error');
    }
  };

  const switchCamera = async () => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacing);
    if (cameraActive) {
      // Stop scanning if active
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      setScanning(false);
      await startCamera(newFacing);
    }
  };

  // Video elementi renderlandiginda stream'i bagla
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraActive, facingMode]);

  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setScanning(false);
    setScanState('idle');
  };

  const autoReset = (delay = 4000) => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      if (scanning) {
        setScanState('scanning');
      }
      setLastPunch(null);
    }, delay);
  };

  // Attendance mode - continuous scan
  const startScanning = () => {
    if (!cameraActive || embeddings.length === 0) {
      setErrorMessage(t('faceAttendance.cameraOpenAndFaceRequired'));
      return;
    }
    setScanning(true);
    setScanState('scanning');

    const labeledDescriptors = embeddings
      .filter(e => e.descriptors.length > 0)
      .map(e => {
        const descriptors = e.descriptors.map(d => new Float32Array(d.values));
        return new faceapi.LabeledFaceDescriptors(`${e.employeeId}|${e.name}`, descriptors);
      });

    if (labeledDescriptors.length === 0) {
      setErrorMessage(t('faceAttendance.noFaceDataFound'));
      setScanning(false);
      setScanState('idle');
      return;
    }

    const faceMatcher = new faceapi.FaceMatcher(labeledDescriptors, MATCH_THRESHOLD);
    faceMatcherRef.current = faceMatcher;

    beginScanLoop(faceMatcher);
  };

  const beginScanLoop = (faceMatcher: any) => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);

    scanIntervalRef.current = window.setInterval(async () => {
      if (!videoRef.current) return;

      try {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();

        // Canvas uzerine yuz cercevesi ciz
        if (canvasRef.current && videoRef.current) {
          const dims = faceapi.matchDimensions(canvasRef.current, videoRef.current, true);
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

            if (detection) {
              const resized = faceapi.resizeResults(detection, dims);
              const rBox = resized.detection.box;
              const match = faceMatcher.findBestMatch(detection.descriptor);
              const isMatch = match.label !== 'unknown';

              // Kare - kalin cerceve
              ctx.strokeStyle = isMatch ? '#22c55e' : '#ef4444';
              ctx.lineWidth = 5;
              // Yuz karesini %30 buyut
              const padding = rBox.width * 0.15;
              ctx.strokeRect(rBox.x - padding, rBox.y - padding, rBox.width + padding * 2, rBox.height + padding * 2);

              // Koselerden kalin cizgiler (modern gorunum)
              const cornerLen = 20;
              ctx.lineWidth = 6;
              const bx = rBox.x - padding, by = rBox.y - padding, bw = rBox.width + padding * 2, bh = rBox.height + padding * 2;
              // Sol ust
              ctx.beginPath(); ctx.moveTo(bx, by + cornerLen); ctx.lineTo(bx, by); ctx.lineTo(bx + cornerLen, by); ctx.stroke();
              // Sag ust
              ctx.beginPath(); ctx.moveTo(bx + bw - cornerLen, by); ctx.lineTo(bx + bw, by); ctx.lineTo(bx + bw, by + cornerLen); ctx.stroke();
              // Sol alt
              ctx.beginPath(); ctx.moveTo(bx, bh + by - cornerLen); ctx.lineTo(bx, bh + by); ctx.lineTo(bx + cornerLen, bh + by); ctx.stroke();
              // Sag alt
              ctx.beginPath(); ctx.moveTo(bx + bw - cornerLen, bh + by); ctx.lineTo(bx + bw, bh + by); ctx.lineTo(bx + bw, bh + by - cornerLen); ctx.stroke();

              // Isim etiketi
              ctx.font = 'bold 20px Inter, sans-serif';
              ctx.fillStyle = isMatch ? '#22c55e' : '#ef4444';
              const label = isMatch ? match.label.split('|')[1] : t('faceAttendance.unrecognized');
              // Arka plan
              const textWidth = ctx.measureText(label).width;
              ctx.fillStyle = 'rgba(0,0,0,0.6)';
              ctx.fillRect(bx, by - 32, textWidth + 16, 28);
              ctx.fillStyle = isMatch ? '#22c55e' : '#ef4444';
              ctx.fillText(label, bx + 8, by - 12);

              // Punch - tanidiysa taramayi durdur, kaydet, 5sn sonra tekrar basla
              if (isMatch && Date.now() - lastPunchTimeRef.current > 5000) {
                lastPunchTimeRef.current = Date.now();
                const empId = parseInt(match.label.split('|')[0]);
                const confidence = 1 - match.distance;

                // Taramayi durdur
                if (scanIntervalRef.current) {
                  clearInterval(scanIntervalRef.current);
                  scanIntervalRef.current = null;
                }

                try {
                  const res = await faceService.punch(empId, confidence);
                  if (res.success && res.data) {
                    setLastPunch(res.data);
                    const dir = res.data.direction;
                    setScanState(dir === 'GIRIS' ? 'success_in' : 'success_out');

                    setTodayLogs(prev => [{
                      employeeName: res.data.employeeName,
                      direction: dir,
                      time: res.data.time,
                      confidence
                    }, ...prev]);

                    setTodayStats(prev => ({
                      totalIn: prev.totalIn + (dir === 'GIRIS' ? 1 : 0),
                      totalOut: prev.totalOut + (dir === 'CIKIS' ? 1 : 0),
                      uniqueEmployees: prev.uniqueEmployees + (dir === 'GIRIS' ? 1 : 0),
                    }));

                    // Tarama durdu - canvas ve overlay temizle
                    setScanning(false);
                    if (canvasRef.current) {
                      const c = canvasRef.current.getContext('2d');
                      c?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                    }
                    // 3 saniye sonra punch bilgisini temizle
                    setTimeout(() => {
                      setLastPunch(null);
                      setScanState('idle');
                    }, 3000);
                  }
                } catch (e) {
                  console.error('Punch hatasi:', e);
                  setScanning(false);
                  if (canvasRef.current) {
                    const c = canvasRef.current.getContext('2d');
                    c?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                  }
                }
                return;
              }
            }
          }
        }
      } catch {
        // Sessiz hata - tarama devam etsin
      }
    }, 1000);
  };

  const stopScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setScanning(false);
    setScanState('idle');
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  // Guided register - auto capture with countdown
  const startGuidedRegister = () => {
    if (!selectedEmployeeId) {
      setErrorMessage(t('faceAttendance.selectEmployeeFirst'));
      return;
    }
    setCapturedDescriptors([]);
    setRegisterStep(0);
    setAutoCapturing(true);
    setStepComplete(false);
    setErrorMessage('');
    setStatusMessage('');
    // Start countdown for first step
    startStepCountdown(0);
  };

  const startStepCountdown = (step: number) => {
    if (step >= REGISTER_STEP_KEYS.length) {
      setAutoCapturing(false);
      return;
    }
    setRegisterStep(step);
    setStepComplete(false);
    setCaptureCountdown(3);

    let count = 3;
    if (autoCaptureRef.current) clearInterval(autoCaptureRef.current);

    autoCaptureRef.current = window.setInterval(() => {
      count--;
      if (count > 0) {
        setCaptureCountdown(count);
      } else {
        setCaptureCountdown(null);
        if (autoCaptureRef.current) clearInterval(autoCaptureRef.current);
        captureCurrentStep(step);
      }
    }, 1000);
  };

  const captureCurrentStep = async (step: number) => {
    if (!videoRef.current) return;

    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setErrorMessage(t('faceAttendance.faceNotDetectedRetry'));
        // Retry after 1.5s
        setTimeout(() => {
          setErrorMessage('');
          startStepCountdown(step);
        }, 1500);
        return;
      }

      const stepInfo = REGISTER_STEP_KEYS[step];
      setCapturedDescriptors(prev => [...prev, { label: stepInfo.label, values: Array.from(detection.descriptor) }]);
      setStepComplete(true);
      setErrorMessage('');

      // Move to next step after 1s
      setTimeout(() => {
        const nextStep = step + 1;
        if (nextStep < REGISTER_STEP_KEYS.length) {
          startStepCountdown(nextStep);
        } else {
          setAutoCapturing(false);
          setStatusMessage(t('faceAttendance.allAnglesComplete'));
        }
      }, 1000);
    } catch (err: any) {
      setErrorMessage(`${t('faceAttendance.captureError')}: ${err.message}`);
      setTimeout(() => {
        setErrorMessage('');
        startStepCountdown(step);
      }, 1500);
    }
  };

  const cancelGuidedRegister = () => {
    if (autoCaptureRef.current) clearInterval(autoCaptureRef.current);
    setAutoCapturing(false);
    setCaptureCountdown(null);
    setCapturedDescriptors([]);
    setRegisterStep(0);
    setStepComplete(false);
  };

  // Manual single capture (fallback)
  const captureForRegister = async () => {
    if (!videoRef.current || !selectedEmployeeId) {
      setErrorMessage(t('faceAttendance.startCameraAndSelect'));
      return;
    }
    setStatusMessage(t('faceAttendance.faceDetecting'));
    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setErrorMessage(t('faceAttendance.faceNotDetected'));
        return;
      }

      const label = REGISTER_STEP_KEYS[capturedDescriptors.length]?.label || `ek-${capturedDescriptors.length}`;
      setCapturedDescriptors(prev => [...prev, { label, values: Array.from(detection.descriptor) }]);
      setStatusMessage(`${capturedDescriptors.length + 1}. ${t('faceAttendance.faceDataCount')} (${label}).`);
      setErrorMessage('');
    } catch (err: any) {
      setErrorMessage(`${t('faceAttendance.captureError')}: ${err.message}`);
    }
  };

  const saveRegister = async () => {
    if (capturedDescriptors.length < 1) {
      setErrorMessage(t('faceAttendance.minOneFaceRequired'));
      return;
    }
    try {
      setRegisterSaving(true);
      const res = await faceService.register(selectedEmployeeId, capturedDescriptors, true);
      if (res.success) {
        setStatusMessage(`${t('faceAttendance.faceRegComplete')} ${capturedDescriptors.length} ${t('faceAttendance.dataSaved')}`);
        setCapturedDescriptors([]);
        setSelectedEmployeeId(0);
      } else {
        setErrorMessage(res.message || t('faceAttendance.registrationError'));
      }
    } catch (err: any) {
      setErrorMessage(err.message || t('faceAttendance.registrationError'));
    } finally {
      setRegisterSaving(false);
    }
  };

  const switchMode = (newMode: Mode) => {
    stopScanning();
    if (autoCaptureRef.current) clearInterval(autoCaptureRef.current);
    setAutoCapturing(false);
    setCaptureCountdown(null);
    setRegisterStep(0);
    setStepComplete(false);
    setLastPunch(null);
    setErrorMessage('');
    setStatusMessage('');
    setCapturedDescriptors([]);
    setSelectedEmployeeId(0);
    setMode(newMode);
  };

  const formatTime = (date: Date) => date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formatDate = (date: Date) => date.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col select-none">
      {/* Header */}
      <header className="px-4 py-3 flex items-center justify-between bg-black/20 backdrop-blur-sm">
        <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-white/10 transition">
          <ArrowLeft size={20} />
        </button>
        <div className="text-center">
          <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">{t('faceAttendance.title')}</div>
          <div className="text-sm font-medium text-slate-300">{formatDate(currentTime)}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-mono font-bold tabular-nums">{formatTime(currentTime)}</div>
        </div>
      </header>

      {/* Mode Tabs */}
      <div className="px-4 pt-3 flex gap-2">
        <button
          onClick={() => switchMode('attendance')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
            mode === 'attendance' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'
          }`}
        >
          <ScanFace size={14} /> {t('faceAttendance.attendanceTab')}
        </button>
        <button
          onClick={() => switchMode('register')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
            mode === 'register' ? 'bg-emerald-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'
          }`}
        >
          <UserPlus size={14} /> {t('faceAttendance.registerTab')}
        </button>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-4">
        {/* Loading */}
        {loadingModels && (
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse mb-4 mx-auto">
              <Loader2 size={48} className="text-blue-400 animate-spin" />
            </div>
            <p className="text-sm text-slate-300 font-bold">{t('faceAttendance.loadingModels')}</p>
            <p className="text-xs text-slate-500 mt-1">{t('faceAttendance.firstLoadNote')}</p>
            {backendName && (
              <p className="text-xs text-slate-500 mt-1">{t('faceAttendance.backend')}: {backendName}{backendName === 'cpu' ? ` (${t('faceAttendance.backendSlow')})` : ''}</p>
            )}
          </div>
        )}

        {/* Fatal error - models could not load */}
        {!loadingModels && !modelsLoaded && scanState === 'error' && (
          <div className="text-center max-w-md mx-auto">
            <div className="w-32 h-32 rounded-full bg-red-500/20 flex items-center justify-center mb-4 mx-auto">
              <XCircle size={48} className="text-red-400" />
            </div>
            <p className="text-sm text-red-300 font-bold mb-2">{t('faceAttendance.faceRecInitFailed')}</p>
            <p className="text-xs text-slate-400 mb-4">{errorMessage}</p>
            <button
              onClick={() => { setErrorMessage(''); loadModels(); }}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm font-bold flex items-center gap-2 mx-auto transition"
            >
              <RotateCcw size={16} /> {t('faceAttendance.retry')}
            </button>
          </div>
        )}

        {modelsLoaded && backendName === 'cpu' && (
          <div className="w-full max-w-md mx-auto mb-2 bg-amber-600/10 border border-amber-500/20 rounded-xl px-4 py-2 text-amber-300 text-xs font-medium text-center">
            {t('faceAttendance.webglNotSupported')}
          </div>
        )}

        {modelsLoaded && mode === 'attendance' && (
          <>
            {/* Camera + Scan Overlay */}
            {cameraActive ? (
              <div className="w-full max-w-md mx-auto">
                {/* Camera View */}
                <div className="relative bg-black rounded-2xl overflow-hidden aspect-square">
                  <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                  <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ background: 'transparent' }} />

                  {/* Camera switch button */}
                  <button
                    onClick={switchCamera}
                    className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all z-10"
                    title={facingMode === 'user' ? t('faceAttendance.backCamera') : t('faceAttendance.frontCamera')}
                  >
                    <FlipHorizontal2 size={18} className="text-white" />
                  </button>

                  {/* Scanning indicator */}
                  {scanning && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-green-600/90 px-3 py-1.5 rounded-lg">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      <span className="text-[10px] font-bold uppercase">{t('faceAttendance.scanActive')}</span>
                    </div>
                  )}

                  {/* Scan State Overlay */}
                  {(scanState === 'success_in' || scanState === 'success_out') && lastPunch && (
                    <div className={`absolute inset-x-0 bottom-0 p-4 ${
                      scanState === 'success_in' ? 'bg-gradient-to-t from-emerald-900/90 to-transparent' : 'bg-gradient-to-t from-amber-900/90 to-transparent'
                    }`}>
                      <div className="text-center">
                        <p className={`text-2xl font-black ${scanState === 'success_in' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {lastPunch.direction === 'GIRIS' ? t('faceAttendance.entry') : t('faceAttendance.exit')}
                        </p>
                        <p className="text-lg font-bold text-white mt-1">{lastPunch.employeeName}</p>
                        <p className="text-sm font-mono text-white/80">{lastPunch.time}</p>
                        <p className="text-[10px] text-white/50 mt-1">{t('faceAttendance.confidence')}: %{(lastPunch.confidence * 100).toFixed(0)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex gap-3 mt-3">
                  {!scanning ? (
                    <button
                      onClick={startScanning}
                      className="flex-1 py-3 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                    >
                      <Play size={18} /> {t('faceAttendance.startScan')}
                    </button>
                  ) : (
                    <button
                      onClick={stopScanning}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                    >
                      <Square size={18} /> {t('faceAttendance.stopScan')}
                    </button>
                  )}
                  <button
                    onClick={stopCamera}
                    className="px-5 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm transition-all"
                  >
                    {t('faceAttendance.closeCamera')}
                  </button>
                </div>

                {embeddings.length > 0 && (
                  <p className="text-[10px] text-slate-500 text-center mt-2">{embeddings.length} {t('faceAttendance.faceDataLoaded')}</p>
                )}
              </div>
            ) : (
              /* Idle - Camera not started */
              <div className="text-center">
                <div className={`w-48 h-48 rounded-full flex items-center justify-center mx-auto transition-all duration-500 ${
                  scanState === 'error' ? 'bg-red-500/20' : 'bg-slate-700/30'
                }`}>
                  {scanState === 'error' ? (
                    <XCircle size={72} className="text-red-400" />
                  ) : (
                    <ScanFace size={72} className="text-slate-500" />
                  )}
                </div>
                <div className="mt-6">
                  <button
                    onClick={() => startCamera()}
                    className="px-8 py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-2xl text-lg font-bold flex items-center gap-3 mx-auto transition shadow-lg shadow-blue-600/30"
                  >
                    <Camera size={24} /> {t('faceAttendance.startCamera')}
                  </button>
                  {errorMessage && (
                    <p className="text-red-400 text-xs mt-3 max-w-xs mx-auto">{errorMessage}</p>
                  )}
                  {!errorMessage && (
                    <p className="text-sm text-slate-400 mt-3">{t('faceAttendance.cameraStartHint')}</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Register Mode */}
        {modelsLoaded && mode === 'register' && (
          <div className="w-full max-w-md mx-auto">
            {cameraActive ? (
              <>
                <div className="relative bg-black rounded-2xl overflow-hidden aspect-square">
                  <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                  <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ background: 'transparent' }} />

                  {/* Camera switch button */}
                  <button
                    onClick={switchCamera}
                    className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all z-10"
                    title={facingMode === 'user' ? t('faceAttendance.backCamera') : t('faceAttendance.frontCamera')}
                  >
                    <FlipHorizontal2 size={18} className="text-white" />
                  </button>

                  {/* Guided registration overlay */}
                  {autoCapturing && registerStep < REGISTER_STEP_KEYS.length && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      {/* Step instruction */}
                      <div className="bg-black/70 backdrop-blur-sm rounded-2xl px-6 py-4 text-center">
                        <div className="text-4xl mb-2">{REGISTER_STEP_KEYS[registerStep].icon}</div>
                        <p className="text-xl font-black text-white">{t(`faceAttendance.${REGISTER_STEP_KEYS[registerStep].instructionKey}`)}</p>
                        <p className="text-xs text-slate-300 mt-1">{t(`faceAttendance.${REGISTER_STEP_KEYS[registerStep].descKey}`)}</p>
                        {captureCountdown !== null && (
                          <div className="mt-3">
                            <span className="text-5xl font-black text-emerald-400 tabular-nums animate-pulse">{captureCountdown}</span>
                          </div>
                        )}
                        {stepComplete && (
                          <div className="mt-3 flex items-center justify-center gap-2 text-emerald-400">
                            <CheckCircle size={20} />
                            <span className="font-bold text-sm">{t('faceAttendance.captured')}</span>
                          </div>
                        )}
                      </div>
                      {/* Step counter */}
                      <div className="mt-3 bg-black/60 rounded-full px-4 py-1.5">
                        <span className="text-xs font-bold text-slate-300">
                          {registerStep + 1} / {REGISTER_STEP_KEYS.length}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Progress steps */}
                <div className="flex gap-1 mt-3">
                  {REGISTER_STEP_KEYS.map((step, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-full h-1.5 rounded-full transition-all ${
                        i < capturedDescriptors.length ? 'bg-emerald-500' :
                        i === registerStep && autoCapturing ? 'bg-emerald-500/50 animate-pulse' :
                        'bg-white/10'
                      }`} />
                      <span className={`text-[9px] font-bold ${
                        i < capturedDescriptors.length ? 'text-emerald-400' :
                        i === registerStep && autoCapturing ? 'text-white' :
                        'text-slate-600'
                      }`}>{t(`faceAttendance.${step.instructionKey}`)}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mt-3">
                  <select
                    value={selectedEmployeeId}
                    onChange={(e) => {
                      setSelectedEmployeeId(parseInt(e.target.value) || 0);
                      setCapturedDescriptors([]);
                      cancelGuidedRegister();
                    }}
                    disabled={autoCapturing}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:ring-2 focus:ring-emerald-500 outline-none disabled:opacity-50"
                  >
                    <option value={0} className="bg-slate-900">{t('faceAttendance.selectEmployee')}</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id} className="bg-slate-900">
                        {e.name} {e.departmentName ? `(${e.departmentName})` : ''}
                      </option>
                    ))}
                  </select>

                  {selectedEmployeeId > 0 && (
                    <>
                      {!autoCapturing && capturedDescriptors.length === 0 && (
                        <button
                          onClick={startGuidedRegister}
                          className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-600/20"
                        >
                          <ScanFace size={18} /> {t('faceAttendance.startFaceRegister')}
                        </button>
                      )}

                      {autoCapturing && (
                        <button
                          onClick={cancelGuidedRegister}
                          className="w-full py-3 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                        >
                          <XCircle size={16} /> {t('faceAttendance.cancelRegister')}
                        </button>
                      )}

                      {!autoCapturing && capturedDescriptors.length > 0 && (
                        <div className="flex gap-2">
                          <button
                            onClick={saveRegister}
                            disabled={registerSaving}
                            className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                          >
                            {registerSaving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                            {t('faceAttendance.saveRegister')} ({capturedDescriptors.length} {t('faceAttendance.angles')})
                          </button>
                          <button
                            onClick={() => { setCapturedDescriptors([]); setRegisterStep(0); }}
                            className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                          >
                            <RotateCcw size={16} /> {t('faceAttendance.againRegister')}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => { cancelGuidedRegister(); stopCamera(); }}
                    className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm transition-all"
                  >
                    {t('faceAttendance.closeTheCamera')}
                  </button>
                </div>

                {/* Status */}
                {statusMessage && (
                  <div className="mt-3 bg-emerald-600/10 border border-emerald-500/20 rounded-xl px-4 py-2.5 text-emerald-300 text-xs font-medium flex items-center gap-2">
                    <CheckCircle size={14} /> {statusMessage}
                  </div>
                )}
                {errorMessage && (
                  <div className="mt-3 bg-red-600/10 border border-red-500/20 rounded-xl px-4 py-2.5 text-red-300 text-xs font-medium">
                    {errorMessage}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center">
                <div className="w-48 h-48 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                  <UserPlus size={72} className="text-emerald-500/50" />
                </div>
                <button
                  onClick={() => startCamera()}
                  className="mt-6 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-2xl text-lg font-bold flex items-center gap-3 mx-auto transition shadow-lg shadow-emerald-600/30"
                >
                  <Camera size={24} /> {t('faceAttendance.startCamera')}
                </button>
                <div className="mt-6 bg-white/5 rounded-2xl p-4 text-left">
                  <p className="font-bold text-xs mb-2 text-slate-300">{t('faceAttendance.guidedFaceRegister')}</p>
                  <ol className="space-y-1.5 text-xs text-slate-500">
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                      {t('faceAttendance.step1')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold shrink-0">2</span>
                      {t('faceAttendance.step2')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold shrink-0">3</span>
                      {t('faceAttendance.step3')}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold shrink-0">4</span>
                      {t('faceAttendance.step4')}
                    </li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Bar - only in attendance mode */}
      {mode === 'attendance' && (
        <div className="px-4 py-3 bg-black/30 backdrop-blur-sm flex items-center justify-around border-t border-white/5">
          <div className="text-center">
            <div className="flex items-center gap-1.5 justify-center">
              <LogIn size={14} className="text-emerald-400" />
              <span className="text-2xl font-bold tabular-nums">{todayStats.totalIn}</span>
            </div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('faceAttendance.checkIn')}</p>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <div className="flex items-center gap-1.5 justify-center">
              <LogOut size={14} className="text-amber-400" />
              <span className="text-2xl font-bold tabular-nums">{todayStats.totalOut}</span>
            </div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('faceAttendance.checkOut')}</p>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <div className="flex items-center gap-1.5 justify-center">
              <Users size={14} className="text-blue-400" />
              <span className="text-2xl font-bold tabular-nums">{todayStats.uniqueEmployees}</span>
            </div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('faceAttendance.personnel')}</p>
          </div>
        </div>
      )}

      {/* Today's Log List - only in attendance mode */}
      {mode === 'attendance' && todayLogs.length > 0 && (
        <div className="max-h-[30vh] overflow-y-auto bg-black/20">
          <div className="px-4 py-2 sticky top-0 bg-slate-900/90 backdrop-blur-sm border-b border-white/5">
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('faceAttendance.todayRecords')} ({todayLogs.length})</p>
          </div>
          <div className="divide-y divide-white/5">
            {todayLogs.map((log, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{log.employeeName}</p>
                  <p className="text-[10px] text-slate-500">{t('faceAttendance.confidence')}: %{(log.confidence * 100).toFixed(0)}</p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <span className={`flex items-center gap-1 text-xs font-bold ${
                    log.direction === 'GIRIS' ? 'text-emerald-400' : 'text-amber-400'
                  }`}>
                    {log.direction === 'GIRIS' ? <LogIn size={12} /> : <LogOut size={12} />}
                    {log.direction === 'GIRIS' ? t('faceAttendance.entry') : t('faceAttendance.exit')}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{log.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceAttendance;
